import React, { useState, useRef } from 'react';
import {
  Box, TextField, Button, Typography, IconButton, Paper,
  Alert, Chip, Select, MenuItem, FormControl, InputLabel,
  Tooltip, Divider, Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

const TASK_TYPES = [
  { value: 'manual', label: 'Manual' },
  { value: 'document', label: 'Document' },
  { value: 'approval', label: 'Approval' },
  { value: 'communication', label: 'Communication' },
  { value: 'automated', label: 'Automated' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'success' },
  { value: 'medium', label: 'Medium', color: 'warning' },
  { value: 'high', label: 'High', color: 'error' },
  { value: 'urgent', label: 'Urgent', color: 'error' },
];

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'tax_preparation', label: 'Tax Preparation' },
  { value: 'client_onboarding', label: 'Client Onboarding' },
  { value: 'document_collection', label: 'Document Collection' },
  { value: 'compliance', label: 'Compliance' },
];

const priorityColor = (p) => PRIORITIES.find((x) => x.value === p)?.color || 'default';

const emptyNewTask = () => ({
  name: '',
  description: '',
  task_type: 'manual',
  priority: 'medium',
  due_date_offset: 0,
  due_date_unit: 'days',
});

const WorkflowTemplateEditor = ({ template, onSave, onCancel }) => {
  const [name, setName]               = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [category, setCategory]       = useState(template?.category || 'general');
  const [durationDays, setDurationDays] = useState(template?.estimated_duration_days || 7);
  const [tasks, setTasks]             = useState(
    (template?.tasks || []).map((t, i) => ({ ...t, _key: i }))
  );
  const [editIdx, setEditIdx]         = useState(null);
  const [editData, setEditData]       = useState({});
  const [newTask, setNewTask]         = useState(emptyNewTask());
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError]             = useState('');
  const [snackbar, setSnackbar]       = useState(false);

  // Drag state
  const dragIdx = useRef(null);
  const dropIdx = useRef(null);

  // ── Drag & Drop ────────────────────────────────────────────────────────────
  const handleDragStart = (idx) => { dragIdx.current = idx; };
  const handleDragOver  = (e, idx) => { e.preventDefault(); dropIdx.current = idx; };
  const handleDrop      = () => {
    const from = dragIdx.current;
    const to   = dropIdx.current;
    if (from === null || to === null || from === to) return;
    const reordered = [...tasks];
    const [moved]   = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setTasks(reordered.map((t, i) => ({ ...t, order: i + 1 })));
    dragIdx.current = null;
    dropIdx.current = null;
  };

  // ── Task CRUD ──────────────────────────────────────────────────────────────
  const handleAddTask = () => {
    if (!newTask.name.trim()) return;
    setTasks((prev) => [
      ...prev,
      { ...newTask, order: prev.length + 1, _key: Date.now() },
    ]);
    setNewTask(emptyNewTask());
    setShowAddForm(false);
    setError('');
  };

  const handleStartEdit = (idx) => {
    setEditIdx(idx);
    setEditData({ ...tasks[idx] });
  };

  const handleSaveEdit = (idx) => {
    if (!editData.name?.trim()) return;
    const updated = [...tasks];
    updated[idx] = { ...updated[idx], ...editData };
    setTasks(updated);
    setEditIdx(null);
    setEditData({});
  };

  const handleCancelEdit = () => { setEditIdx(null); setEditData({}); };

  const handleDeleteTask = (idx) => {
    setTasks((prev) => prev.filter((_, i) => i !== idx).map((t, i) => ({ ...t, order: i + 1 })));
    if (editIdx === idx) setEditIdx(null);
    setError('');
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = () => {
    if (!name.trim()) { setError('Template name is required.'); return; }
    if (tasks.length === 0) { setError('At least one task is required.'); return; }
    setError('');
    setSnackbar(true);
    if (onSave) onSave({ name, description, category, estimated_duration_days: Number(durationDays), tasks });
  };

  return (
    <Box sx={{ p: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {/* ── Template header fields ── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          label="Template Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          required
          sx={{ flex: '1 1 220px' }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          label="Est. Duration (days)"
          type="number"
          value={durationDays}
          onChange={(e) => setDurationDays(e.target.value)}
          size="small"
          sx={{ width: 160 }}
          inputProps={{ min: 1 }}
        />
      </Box>
      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        rows={2}
        size="small"
        sx={{ mb: 3 }}
      />

      {/* ── Task list ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>Tasks ({tasks.length})</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setShowAddForm((v) => !v)}
        >
          Add Task
        </Button>
      </Box>

      {/* Add task inline form */}
      {showAddForm && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: 'primary.main', borderWidth: 1.5 }}>
          <Typography variant="caption" fontWeight={700} color="primary" display="block" mb={1.5}>
            New Task
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
            <TextField
              label="Task Name"
              value={newTask.name}
              onChange={(e) => setNewTask((p) => ({ ...p, name: e.target.value }))}
              size="small"
              sx={{ flex: '1 1 200px' }}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddTask(); }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Type</InputLabel>
              <Select value={newTask.task_type} label="Type" onChange={(e) => setNewTask((p) => ({ ...p, task_type: e.target.value }))}>
                {TASK_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Priority</InputLabel>
              <Select value={newTask.priority} label="Priority" onChange={(e) => setNewTask((p) => ({ ...p, priority: e.target.value }))}>
                {PRIORITIES.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Due offset"
              type="number"
              value={newTask.due_date_offset}
              onChange={(e) => setNewTask((p) => ({ ...p, due_date_offset: e.target.value }))}
              size="small"
              sx={{ width: 100 }}
              inputProps={{ min: 0 }}
              helperText="days from start"
            />
          </Box>
          <TextField
            label="Description (optional)"
            value={newTask.description}
            onChange={(e) => setNewTask((p) => ({ ...p, description: e.target.value }))}
            fullWidth
            multiline
            rows={2}
            size="small"
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleAddTask} disabled={!newTask.name.trim()}>
              Add
            </Button>
            <Button size="small" onClick={() => { setShowAddForm(false); setNewTask(emptyNewTask()); }}>
              Cancel
            </Button>
          </Box>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {tasks.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography variant="body2" color="text.disabled">
              No tasks yet. Click "Add Task" to create the first one.
            </Typography>
          </Box>
        ) : (
          tasks.map((task, idx) => (
            <Box
              key={task._key ?? idx}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={handleDrop}
              sx={{
                borderBottom: idx < tasks.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                bgcolor: editIdx === idx ? 'action.hover' : 'transparent',
                transition: 'background 0.15s',
              }}
            >
              {editIdx === idx ? (
                /* Inline edit row */
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                    <TextField
                      label="Name"
                      value={editData.name || ''}
                      onChange={(e) => setEditData((p) => ({ ...p, name: e.target.value }))}
                      size="small"
                      sx={{ flex: '1 1 200px' }}
                      autoFocus
                    />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Type</InputLabel>
                      <Select value={editData.task_type || 'manual'} label="Type" onChange={(e) => setEditData((p) => ({ ...p, task_type: e.target.value }))}>
                        {TASK_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Priority</InputLabel>
                      <Select value={editData.priority || 'medium'} label="Priority" onChange={(e) => setEditData((p) => ({ ...p, priority: e.target.value }))}>
                        {PRIORITIES.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Due offset"
                      type="number"
                      value={editData.due_date_offset ?? 0}
                      onChange={(e) => setEditData((p) => ({ ...p, due_date_offset: e.target.value }))}
                      size="small"
                      sx={{ width: 100 }}
                      inputProps={{ min: 0 }}
                      helperText="days from start"
                    />
                  </Box>
                  <TextField
                    label="Description"
                    value={editData.description || ''}
                    onChange={(e) => setEditData((p) => ({ ...p, description: e.target.value }))}
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    sx={{ mb: 1.5 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" startIcon={<CheckIcon />} onClick={() => handleSaveEdit(idx)}>
                      Save
                    </Button>
                    <Button size="small" startIcon={<CloseIcon />} onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                /* Read-only task row */
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 1.25,
                    gap: 1,
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 18, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {idx + 1}. {task.name}
                      </Typography>
                      <Chip
                        label={task.task_type || 'manual'}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11, height: 20 }}
                      />
                      <Chip
                        label={task.priority || 'medium'}
                        color={priorityColor(task.priority || 'medium')}
                        size="small"
                        sx={{ fontSize: 11, height: 20 }}
                      />
                      {task.due_date_offset > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          +{task.due_date_offset}d
                        </Typography>
                      )}
                    </Box>
                    {task.description && (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {task.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleStartEdit(idx)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDeleteTask(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Box>
          ))
        )}
      </Paper>

      {tasks.length > 1 && (
        <Typography variant="caption" color="text.disabled" display="block" mt={0.75} ml={0.5}>
          Drag rows to reorder tasks
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />
      <Box display="flex" gap={2} justifyContent="flex-end">
        <Button onClick={onCancel} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleSave} sx={{ borderRadius: 2, fontWeight: 600 }}>
          Save Template
        </Button>
      </Box>

      <Snackbar
        open={snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(false)}
        message="Template saved successfully"
      />
    </Box>
  );
};

export default WorkflowTemplateEditor;
